// ABOUTME: All / Me / <platform> switch above the Marketing lead's thread. Only
// shown once an outside platform has sent a request. A row of chips that scrolls
// sideways when it overflows, so every option keeps its full label and a 44px
// target however many platforms there are; the active chip is kept on screen.
import { useEffect, useRef } from 'react'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import type { PlatformSource } from '@/api/agent'

interface ThreadSourceFilterProps {
  /** 'all', 'me' or a platform id. */
  value: string
  sources: PlatformSource[]
  onChange: (value: string) => void
}

function slug(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export function ThreadSourceFilter({ value, sources, onChange }: ThreadSourceFilterProps) {
  const rowRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const row = rowRef.current
    const active = row?.querySelector<HTMLElement>('[aria-selected="true"]')
    if (!row || !active) return
    const overflowLeft = active.offsetLeft - row.scrollLeft
    const overflowRight = overflowLeft + active.offsetWidth - row.clientWidth
    if (overflowLeft < 0) row.scrollLeft += overflowLeft
    else if (overflowRight > 0) row.scrollLeft += overflowRight
  }, [value, sources.length])

  if (sources.length === 0) return null
  const options = [
    { value: 'all', label: 'All', testId: 'thread-source-all' },
    { value: 'me', label: 'Me', testId: 'thread-source-me' },
    ...sources.map((s) => ({
      value: s.id,
      label: s.label,
      testId: `thread-source-${slug(s.label)}`,
    })),
  ]
  return (
    <Tabs value={value} onValueChange={onChange} data-testid="thread-source-filter">
      <TabsList
        ref={rowRef}
        aria-label="Filter the conversation by source"
        className="scrollbar-hide relative flex h-auto w-full gap-2 overflow-x-auto rounded-none bg-transparent px-4 pt-2 pb-1"
      >
        {options.map((option) => (
          <TabsTrigger
            key={option.value}
            value={option.value}
            aria-controls={undefined}
            data-testid={option.testId}
            className="border-border data-[state=active]:bg-foreground data-[state=active]:text-background h-11 shrink-0 rounded-full border px-4 shadow-none data-[state=active]:border-transparent data-[state=active]:shadow-none"
          >
            {option.label}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  )
}
